// File: app.component.ts
// Author: Joseph M Diaz
// Date: 4/20/2017
// Desc: Root component for the app.
// Other Contributors: Brandon Shreve

import { Component, OnInit, ViewEncapsulation, ElementRef, ViewChild, HostListener, HostBinding } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { config } from '../config/config';
import { AppService } from './app.service';
import { CgpBreadcrumbsService } from './shared/cgp-breadcrumbs/cgp-breadcrumbs.service';
import { CgpIdleService } from './shared/cgp-idle/cgp-idle.service';
import { CgpAlertDialogService } from './shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { UserService } from '../modules/user.service';
import { getEnv } from '../modules/utility';
import { mescUrl } from '../config/config.tprecm';
import * as HttpStatus from 'http-status-codes';
import { TprecmRoutes } from './components/tprecm/tprecm.routing';

@Component({
   selector: 'cgp-app',
   templateUrl: './app.template.html',
   encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {
   @HostBinding('@.disabled') public animationsDisabled = false;
   @ViewChild('cgpContentContainer', { static: true }) private readonly cgpContentContainer: ElementRef;

   sidenavVisible = false;
   isFullScreen = false;
   isLoggedIn = false;

   constructor(
      private readonly router: Router,
      private readonly appService: AppService,
      private readonly cgpBreadcrumbsService: CgpBreadcrumbsService,
      private readonly cgpIdleService: CgpIdleService,
      private readonly cgpAlertDialogService: CgpAlertDialogService,
      private readonly userService: UserService
   ) {
      this.userService.login()
         .subscribe(
            () => {
               this.cgpBreadcrumbsService.login();
               this.isLoggedIn = true;
            },
            (httpErrorResponse) => {
               if (httpErrorResponse && httpErrorResponse.status === HttpStatus.UNAUTHORIZED) {
                  this.isLoggedIn = true;
                  this.router.navigate([TprecmRoutes.REQUEST_ACCES_FORM]);
               } else {
                  this.cgpAlertDialogService.showAlertDialog({
                     width: '475px',
                     title: 'Something went wrong loading your profile.',
                     message: 'Please try again by clicking the <b>Reload</b> button.',
                     alert: 'danger',
                     closeLabel: 'Reload',
                     disableClose: true,
                     onClosed: () => {
                        window.location.replace(window.location.origin + window.location.pathname);
                     }
                  });

                  sessionStorage.clear();
               }
            }
         );

      this.appService.appComponent = this;
      this.cgpIdleService.startTimers();
   }

   ngOnInit() {
      this.router.events.pipe(
         filter(event => event instanceof NavigationEnd)
      ).subscribe((event) => {
         const sidenavContentContainer = this.getBreadCrumbs(event);

         sidenavContentContainer.scrollTop = 0;
         sidenavContentContainer.scrollLeft = 0;
      });
   }

   /**
    * Updates the cgp-breadcrumbs with the config.breadcrumbs based on what the current end route is.
    * Disables the current route link.
    *
    * @private
    * @param {*} event
    * @returns
    * @memberof AppComponent
    */
   private getBreadCrumbs(event) {
      const url = (event).urlAfterRedirects || '';
      const urlArray = url.split('/');
      const lastRoute = urlArray[urlArray.length - 1];
      const sidenavContentContainer = this.cgpContentContainer.nativeElement.querySelector('.mat-sidenav-content');
      const breadcrumbs: any[] = [];

      // Disables the route based on what the current route is.
      urlArray.forEach(element => {
         if (element === lastRoute && config.breadcrumbs.hasOwnProperty(lastRoute)) {
            if (config.breadcrumbs[lastRoute].link) {
               config.breadcrumbs[lastRoute].link.enabled = false;
            }
         } else if (config.breadcrumbs.hasOwnProperty(element)) {
            config.breadcrumbs[element].link.enabled = true;
         }
         if (config.breadcrumbs.hasOwnProperty(element)) {
            breadcrumbs.push(config.breadcrumbs[element]);
         }
      });

      // If the current env is not local, set the home button to current mesc environment url.
      if (breadcrumbs) {
         if (getEnv() !== 'LOCAL') {
            const homeBreadCrumb = breadcrumbs.find(x => x.name === undefined);
            homeBreadCrumb.link.url = mescUrl[getEnv()];
         }
         this.cgpBreadcrumbsService.updateBreadcrumbs(breadcrumbs);
      } else {
         this.cgpBreadcrumbsService.updateBreadcrumbs(config.breadcrumbs['404']);
      }
      return sidenavContentContainer;
   }

   @HostListener('window:mousemove')
   onMouseMove() {
      this.cgpIdleService.setIsActive();
   }

   @HostListener('window:mousedown')
   onMouseDown() {
      this.cgpIdleService.setIsActive();
   }

   @HostListener('window:keydown')
   onKeyDown() {
      this.cgpIdleService.setIsActive();
   }


   public get IsShowFooter(): boolean {
      return this.router.url === '/tprecm';
   }

}
